import { useQuery } from '@apollo/client'
import { useParams } from 'react-router'
import queries from '../../../../../graphql/queries'
import { GetVehicle_Full, GetVehicle_FullVariables } from '../../../../../graphql/queries/typings/GetVehicle_Full'
import { EnumUser } from '../../../../../graphql/typings/global_types'
import IconHelper from '../../../../helpers/IconHelper'
import RoutesHelper from '../../../../helpers/RoutesHelper'
import VehicleHelper from '../../../../helpers/VehicleHelper'
import { Vehicle_Full } from '../../../../helpers/data/models/fleet/Vehicle'
import ErrorDisplay from '../../../../helpers/errors/ErrorDisplay'
import { useAuthUser } from '../../../../hooks/useAuth'
import useNavContext from '../../../../hooks/useNavContext'
import CustomTabs, { CustomTab } from '../../../CustomTabs'
import LoadingDisplay from '../../../LoadingDisplay'
import VehicleDocuments from './VehicleDocuments'
import VehicleGeneral from './VehicleGeneral'
import VehicleContracts from './contracts/VehicleContracts'

export type VehicleTabName = 'general' | 'contracts' | 'documents'

const Vehicle = () => {
    const auth = useAuthUser()

    const vehicleRoute =
        auth.user.userType === EnumUser.admin
            ? RoutesHelper.views.AEA.customer.vehicles.Vehicle
            : RoutesHelper.views.Customer.vehicles.Vehicle

    // Deserves to update the enterpriseId according to URL params
    const { vehicleId, tab } = useParams()

    return vehicleId !== undefined ? (
        <VehicleParamsOK vehicleId={vehicleId} tab={tab ? (tab as VehicleTabName) : undefined} />
    ) : (
        <ErrorDisplay message="Adresse incorrecte" />
    )
}

type VehicleParamsOKProps = {
    vehicleId: string
    tab: VehicleTabName | undefined
}

const VehicleParamsOK = ({ vehicleId, tab }: VehicleParamsOKProps) => {
    const queryResult = useQuery<GetVehicle_Full, GetVehicle_FullVariables>(queries.GetVehicle_Full, {
        variables: {
            id: vehicleId,
        },
    })

    return queryResult.loading ? (
        <LoadingDisplay />
    ) : !queryResult.data ? (
        <ErrorDisplay message="Erreur lors de la récupération du véhicule" debug={queryResult} />
    ) : (
        <VehicleReady vehicle={queryResult.data.vehicle} tab={tab} />
    )
}

type VehicleReadyProps = {
    vehicle: Vehicle_Full
    tab?: VehicleTabName
}

const VehicleReady = ({ vehicle, tab }: VehicleReadyProps) => {
    const { getVehiclesRoute, getVehicleRoute } = useNavContext()

    const breadcrumbSegments = [
        {
            name: 'Véhicules',
            path: getVehiclesRoute().pathname,
        },
        {
            name: VehicleHelper.getLabel(vehicle),
            icon: IconHelper.elementIcons.vehicleIcon,
            path: getVehicleRoute(vehicle.id).pathname,
        },
    ]

    const tabs: CustomTab<VehicleTabName>[] = [
        {
            id: 'general',
            label: 'Général',
            children: <VehicleGeneral vehicle={vehicle} />,
            to: getVehicleRoute(vehicle.id),
        },
        {
            id: 'contracts',
            label: 'Contrats',
            children: <VehicleContracts vehicleID={vehicle.id} />,
            to: getVehicleRoute(vehicle.id, 'contracts'),
        },
        {
            id: 'documents',
            label: 'Documents',
            children: <VehicleDocuments vehicle={vehicle} />,
            to: getVehicleRoute(vehicle.id, 'documents'),
        },
    ]

    return (
        <CustomTabs<Vehicle_Full, VehicleTabName>
            breadcrumbSegments={breadcrumbSegments}
            activeTab={tab || 'general'}
            tabs={tabs}
        />
    )
}

export default Vehicle
